import { createRouter, createWebHistory } from 'vue-router'
import oidcAuth from '@/auth/oidcAuth'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useLayoutModeStore, HOME_PATH_V1, HOME_PATH_V2 } from '@/stores/useLayoutModeStore'
import { useUserStore } from '@/stores/useUserStore'
import { useCampaignStore } from '@/stores/useCampaignStore'
import { useSelectionsStore } from '@/stores/useSelectionsStore'
import { useTitleStore } from '@/stores/useTitleStore'
import { useKpiStore } from '@/stores/useKpiStore'
import HomePage from '@/pages/HomePage.vue'
import LoginPage from '@/pages/LoginPage.vue'
import ForgotPasswordPage from '@/pages/ForgotPasswordPage.vue'
import ResetPasswordPage from '@/pages/ResetPasswordPage.vue'
import SearchPartnerPage from '@/pages/SearchPartnerPage.vue'
import PartnerPage from '@/pages/PartnerPage.vue'
import DiaryPage from '@/pages/DiaryPage.vue'
import OpportunityPage from '@/pages/OpportunityPage.vue'
import CallingPage from '@/pages/CallingPage.vue'
import ActivitiesPage from '@/pages/ActivitiesPage.vue'
import NewsFeedPage from '@/pages/news/NewsFeedPage.vue'
import KpiPage from '@/pages/KpiPage.vue'
import OidcCallbackPage from '@/pages/OidcCallbackPage.vue'
import DisconnectedPage from '@/pages/DisconnectedPage.vue'
import TicketPage from '@/pages/TicketPage.vue'
import UserNotificationPage from '@/pages/UserNotificationPage.vue'
import VariablePage from '@/pages/VariablePage.vue'
import VariableLuccaAdminPage from '@/pages/VariableLuccaAdminPage.vue'
import DashboardOrderCreatePage from '@/pages/order/DashboardOrderCreatePage.vue'
import DashboardOrdersPage from '@/pages/order/DashboardOrdersPage.vue'
import DashboardDerogationsPage from '@/pages/order/DashboardDerogationsPage.vue'
import DashboardArbitrationsPage from '@/pages/order/DashboardArbitrationsPage.vue'
import CommissionDashboardPage from '@/pages/order/CommissionDashboardPage.vue'
import EligibilityPage from '@/pages/EligibilityPage.vue'
import EligibilityDetailPage from '@/pages/EligibilityDetailPage.vue'
import ClientTicketDetailPage from '@/pages/ClientTicketDetailPage.vue'
import DashboardRegulationCreatePage from '@/pages/regulation/DashboardRegulationCreatePage.vue'
import DashboardRegulationDetailPage from '@/pages/regulation/DashboardRegulationDetailPage.vue'
import UsersAdminPage from '@/pages/admin/UsersAdminPage.vue'
import RegionsAdminPage from '@/pages/admin/RegionsAdminPage.vue'
import BusinessUnitsAdminPage from '@/pages/admin/BusinessUnitsAdminPage.vue'
import TeamsAdminPage from '@/pages/admin/TeamsAdminPage.vue'
import AgenciesAdminPage from '@/pages/admin/AgenciesAdminPage.vue'
import FeatureFlagsAdminPage from '@/pages/admin/FeatureFlagsAdminPage.vue'
import AdminHubPage from '@/pages/admin/AdminHubPage.vue'
import AccessControlAdminPage from '@/pages/admin/AccessControlAdminPage.vue'
import TemplateMailsAdminPage from '@/pages/admin/TemplateMailsAdminPage.vue'
import PortalPermissionsAdminPage from '@/pages/admin/PortalPermissionsAdminPage.vue'
import EspaceClientStatsPage from '@/pages/admin/EspaceClientStatsPage.vue'
import FeatureRegistryAdminPage from '@/pages/admin/FeatureRegistryAdminPage.vue'
import FaqAdminPage from '@/pages/admin/FaqAdminPage.vue'
import LegalPagesAdminPage from '@/pages/admin/LegalPagesAdminPage.vue'
import SourcesAdminPage from '@/pages/admin/SourcesAdminPage.vue'
import TicketsAdminPage from '@/pages/admin/TicketsAdminPage.vue'
import TicketsStatsAdminPage from '@/pages/admin/TicketsStatsAdminPage.vue'
import PoleDevAdminPage from '@/pages/admin/PoleDevAdminPage.vue'
import PoleDevCommunicationsPage from '@/pages/admin/PoleDevCommunicationsPage.vue'
import PoleDevSprintsPage from '@/pages/admin/PoleDevSprintsPage.vue'
import PoleDevAttentionPointsPage from '@/pages/admin/PoleDevAttentionPointsPage.vue'
import ImportsCegidAdminPage from '@/pages/admin/ImportsCegidAdminPage.vue'
import DeliveriesAdminPage from '@/pages/admin/DeliveriesAdminPage.vue'
import QuotesBillingDashboardPage from '@/pages/admin/QuotesBillingDashboardPage.vue'
import H3aStatsAdminPage from '@/pages/admin/H3aStatsAdminPage.vue'
import BannersAdminPage from '@/pages/admin/BannersAdminPage.vue'
import NewsAdminPage from '@/pages/admin/NewsAdminPage.vue'
import FiberLinkDetailPage from '@/pages/admin/FiberLinkDetailPage.vue'
import { v2Routes } from '@/v2/router'
import SearchPage from '@/v2/pages/SearchPage.vue'
import DashboardContactsPage from '@/pages/contact/DashboardContactsPage.vue'
import DashboardContactEditPage from '@/pages/contact/DashboardContactEditPage.vue'
import DashboardTodosPage from '@/pages/todo/DashboardTodosPage.vue'
import DashboardEnvelopesPage from '@/pages/envelope/DashboardEnvelopesPage.vue'
import DashboardLeadsPage from '@/pages/leads/DashboardLeadsPage.vue'
import DashboardLeadDetailPage from '@/pages/leads/DashboardLeadDetailPage.vue'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/oidc-callback',
      name: 'oidc-callback',
      component: OidcCallbackPage,
      meta: {
        isOidcCallback: true,
      },
    },
    {
      path: '/auth/login',
      name: 'login',
      component: LoginPage,
      meta: { isPublic: true },
    },
    {
      // Backward-compat: any old link / hardcoded redirect to /login lands on /auth/login.
      path: '/login',
      redirect: '/auth/login',
    },
    {
      path: '/auth/forgot-password',
      name: 'forgot-password',
      component: ForgotPasswordPage,
      meta: { isPublic: true },
    },
    {
      // Backward-compat for any old hardcoded redirect.
      path: '/forgot-password',
      redirect: '/auth/forgot-password',
    },
    {
      path: '/auth/reset/:token',
      name: 'reset-password',
      component: ResetPasswordPage,
      meta: { isPublic: true },
    },
    {
      // Backward-compat for reset emails already sent with the old URL.
      path: '/login/reset/:token',
      redirect: (to) => `/auth/reset/${to.params.token as string}`,
    },
    {
      path: '/',
      name: 'home',
      component: HomePage,
      meta: {
        authName: oidcAuth.authName,
      },
    },
    {
      path: '/auth/logout',
      name: 'logout',
      component: DisconnectedPage,
    },
    {
      // Backward-compat for any old hardcoded redirect.
      path: '/disconnected',
      redirect: '/auth/logout',
    },
    {
      path: '/notifications',
      name: 'notifications',
      component: UserNotificationPage,
      meta: {
        authName: oidcAuth.authName,
      },
    },
    {
      path: '/search',
      name: 'search',
      component: SearchPartnerPage,
      meta: {
        authName: oidcAuth.authName,
      },
    },
    {
      path: '/partner/:partnerId/:tab?',
      name: 'partner',
      component: PartnerPage,
      meta: {
        authName: oidcAuth.authName,
      },
    },
    {
      // Fiche client 360° (page indépendante, n'altère pas la route 'partner').
      // Pilotée par l'UUID de la company boost-suite (et non l'id Odoo).
      path: '/client/:companyUuid/:tab?',
      name: 'client-360',
      component: () => import('@/pages/partner/PartnerClient360Page.vue'),
      meta: {
        authName: oidcAuth.authName,
      },
    },
    {
      path: '/diary',
      name: 'diary',
      component: DiaryPage,
      meta: {
        authName: oidcAuth.authName,
      },
    },
    {
      path: '/kpi',
      name: 'kpi',
      component: KpiPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
      },
    },
    {
      path: '/opportunity',
      name: 'opportunity',
      component: OpportunityPage,
      meta: {
        authName: oidcAuth.authName,
      },
    },
    {
      path: '/campaign/',
      meta: {
        authName: oidcAuth.authName,
      },
      children: [
        {
          path: '',
          name: 'campaign',
          component: () => import('@/pages/CampaignPage.vue'),
          meta: {
            authName: oidcAuth.authName,
          },
        },
        {
          path: ':campaignId/details',
          name: 'campaign-details',
          component: () => import('@/pages/CampaignPageDetails.vue'),
          meta: {
            authName: oidcAuth.authName,
          },
        },
        {
          path: 'management',
          name: 'campaign-management',
          component: () => import('@/pages/CampaignManagementPage.vue'),
          meta: {
            authName: oidcAuth.authName,
          },
        },
      ],
    },

    // New boost-core-backed campaigns. Coexists with /campaign/* during tests;
    // will replace it once parity is confirmed.
    {
      path: '/campaigns/',
      meta: {
        authName: oidcAuth.authName,
      },
      children: [
        {
          path: '',
          name: 'campaigns',
          component: () => import('@/pages/CampaignsPage.vue'),
          meta: {
            authName: oidcAuth.authName,
          },
        },
        {
          path: ':uuid/details',
          name: 'campaigns-details',
          component: () => import('@/pages/CampaignsDetailsPage.vue'),
          meta: {
            authName: oidcAuth.authName,
          },
        },
        {
          path: 'management',
          name: 'campaigns-management',
          component: () => import('@/pages/CampaignsManagementPage.vue'),
          meta: {
            authName: oidcAuth.authName,
          },
        },
      ],
    },

    {
      path: '/activities',
      name: 'activities',
      component: ActivitiesPage,
      meta: {
        authName: oidcAuth.authName,
      },
    },

    {
      path: '/calling/:partnerId',
      name: 'calling',
      component: CallingPage,
      meta: {
        authName: oidcAuth.authName,
      },
    },
    {
      path: '/news',
      name: 'news',
      component: NewsFeedPage,
      meta: {
        authName: oidcAuth.authName,
        version: 'global',
      },
    },
    // Ancienne URL du changelog (liens/bookmarks) — le fil d'actualité le remplace.
    {
      path: '/changelog',
      redirect: '/news',
    },
    {
      path: '/tickets',
      name: 'tickets',
      component: TicketPage,
      meta: {
        authName: oidcAuth.authName,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/tickets/:ticketId',
      name: 'ticket-detail',
      component: TicketPage,
      meta: {
        authName: oidcAuth.authName,
        requiresSuperAdmin: true,
        helpKey: 'tickets',
      },
    },
    {
      // Consultation d'un ticket sans être admin : description, messages publics,
      // statut/dates et liens vers les fiches contact/client.
      path: '/clients/tickets/:ticketUuid',
      name: 'client-ticket-detail',
      component: ClientTicketDetailPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
      },
    },
    {
      path: '/admin/tickets',
      name: 'admin-tickets',
      component: TicketsAdminPage,
      meta: {
        authName: oidcAuth.authName,
        requiresTicketDevAdmin: true,
      },
    },
    {
      path: '/admin/tickets/stats',
      name: 'admin-tickets-stats',
      component: TicketsStatsAdminPage,
      meta: {
        authName: oidcAuth.authName,
        requiresTicketDevAdmin: true,
      },
    },
    {
      path: '/admin/tickets/:uuid',
      name: 'admin-ticket-detail',
      component: TicketsAdminPage,
      meta: {
        authName: oidcAuth.authName,
        requiresTicketDevAdmin: true,
        helpKey: 'admin-tickets',
      },
    },
    {
      path: '/admin/imports-cegid',
      name: 'admin-imports-cegid',
      component: ImportsCegidAdminPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/admin/pole-dev',
      name: 'admin-pole-dev',
      component: PoleDevAdminPage,
      meta: {
        authName: oidcAuth.authName,
        requiresTicketDevAdmin: true,
      },
    },
    {
      path: '/admin/pole-dev/communications',
      name: 'admin-pole-dev-communications',
      component: PoleDevCommunicationsPage,
      meta: {
        authName: oidcAuth.authName,
        requiresTicketDevAdmin: true,
      },
    },
    {
      path: '/admin/pole-dev/sprints',
      name: 'admin-pole-dev-sprints',
      component: PoleDevSprintsPage,
      meta: {
        authName: oidcAuth.authName,
        requiresTicketDevAdmin: true,
      },
    },
    {
      // Doit être déclaré avant `/admin/pole-dev/:id` sinon `:id` capture `points-attention`.
      path: '/admin/pole-dev/points-attention',
      name: 'admin-pole-dev-attention-points',
      component: PoleDevAttentionPointsPage,
      meta: {
        authName: oidcAuth.authName,
        requiresTicketDevAdmin: true,
      },
    },
    {
      path: '/admin/pole-dev/points-attention/:id',
      name: 'admin-pole-dev-attention-point-detail',
      component: PoleDevAttentionPointsPage,
      meta: {
        authName: oidcAuth.authName,
        requiresTicketDevAdmin: true,
        helpKey: 'admin-pole-dev-attention-points',
      },
    },
    {
      path: '/admin/pole-dev/:id',
      name: 'admin-pole-dev-detail',
      component: PoleDevAdminPage,
      meta: {
        authName: oidcAuth.authName,
        requiresTicketDevAdmin: true,
        helpKey: 'admin-pole-dev',
      },
    },
    {
      path: '/admin/fiber-links',
      name: 'admin-fiber-links',
      component: FiberLinkDetailPage,
      meta: {
        authName: oidcAuth.authName,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/admin/fiber-links/:codeContrat',
      name: 'admin-fiber-link-detail',
      component: FiberLinkDetailPage,
      meta: {
        authName: oidcAuth.authName,
        requiresSuperAdmin: true,
        helpKey: 'admin-fiber-links',
      },
    },

    {
      path: '/dashboard/orders',
      name: 'dashboard-orders',
      component: DashboardOrdersPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresOrderAccess: true,
      },
    },
    {
      path: '/dashboard/derogations',
      name: 'dashboard-derogations',
      component: DashboardDerogationsPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresOrderAccess: true,
      },
    },
    {
      path: '/dashboard/arbitrages',
      name: 'dashboard-arbitrages',
      component: DashboardArbitrationsPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresOrderAccess: true,
      },
    },
    {
      path: '/dashboard/commissions',
      name: 'dashboard-commissions',
      component: CommissionDashboardPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/dashboard/order/create',
      name: 'dashboard-order-create',
      component: DashboardOrderCreatePage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresOrderAccess: true,
        requiresOrderCreate: true,
        helpKey: 'order-edit',
      },
    },
    {
      path: '/dashboard/order/:orderUuid',
      name: 'dashboard-order-detail',
      component: DashboardOrderCreatePage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresOrderAccess: true,
        helpKey: 'order-edit',
      },
    },
    {
      path: '/variables',
      name: 'variables',
      component: VariablePage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresVariableAccess: true,
      },
    },
    {
      path: '/variables/lucca-admin',
      name: 'variables-lucca-admin',
      component: VariableLuccaAdminPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/dashboard/regulations',
      redirect: '/dashboard/orders',
    },
    {
      path: '/dashboard/regulation/create',
      name: 'dashboard-regulation-create',
      component: DashboardRegulationCreatePage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresRegulationAccess: true,
        helpKey: 'regulation-edit',
      },
    },
    {
      path: '/dashboard/regulation/:uuid',
      name: 'dashboard-regulation-detail',
      component: DashboardRegulationDetailPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresRegulationAccess: true,
        helpKey: 'regulation-edit',
      },
    },
    {
      path: '/eligibility',
      name: 'eligibility',
      component: EligibilityPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresEligibilityAccess: true,
      },
    },
    {
      path: '/eligibility/:eligibilityUuid',
      name: 'eligibility-detail',
      component: EligibilityDetailPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresEligibilityAccess: true,
      },
    },
    {
      path: '/admin/users',
      name: 'admin-users',
      component: UsersAdminPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresUserMaster: true,
      },
    },
    {
      path: '/admin/regions',
      name: 'admin-regions',
      component: RegionsAdminPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/admin/business-units',
      name: 'admin-business-units',
      component: BusinessUnitsAdminPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/admin/teams',
      name: 'admin-teams',
      component: TeamsAdminPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/admin/agencies',
      name: 'admin-agencies',
      component: AgenciesAdminPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/dashboard/admin',
      name: 'admin-hub',
      component: AdminHubPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/dashboard/admin/featureflag',
      name: 'admin-feature-flags',
      component: FeatureFlagsAdminPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/dashboard/admin/access-control',
      name: 'admin-access-control',
      component: AccessControlAdminPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/dashboard/admin/brevo-templates',
      name: 'admin-brevo-templates',
      component: TemplateMailsAdminPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/dashboard/admin/portal-permissions',
      name: 'admin-portal-permissions',
      component: PortalPermissionsAdminPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/dashboard/admin/espace-client/stats',
      name: 'admin-espace-client-stats',
      component: EspaceClientStatsPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/dashboard/admin/espace-client/feature-registry',
      name: 'admin-feature-registry',
      component: FeatureRegistryAdminPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/dashboard/admin/espace-client/faq',
      name: 'admin-faq',
      component: FaqAdminPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/dashboard/admin/espace-client/legal-pages',
      name: 'admin-legal-pages',
      component: LegalPagesAdminPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/dashboard/admin/sources',
      name: 'admin-sources',
      component: SourcesAdminPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/dashboard/admin/deliveries',
      name: 'admin-deliveries',
      component: DeliveriesAdminPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresDeliveryAdmin: true,
      },
    },
    {
      path: '/dashboard/admin/quotes-billing',
      name: 'admin-quotes-billing',
      component: QuotesBillingDashboardPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresSalesRead: true,
      },
    },
    {
      path: '/h3a-stats',
      name: 'h3a-stats',
      component: H3aStatsAdminPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresH3aStats: true,
      },
    },
    {
      path: '/dashboard/admin/banners',
      name: 'admin-banners',
      component: BannersAdminPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/dashboard/admin/news',
      name: 'admin-news',
      component: NewsAdminPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/dashboard/contacts',
      name: 'dashboard-contacts',
      component: DashboardContactsPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
      },
    },
    {
      path: '/dashboard/contacts/:contactUuid',
      name: 'dashboard-contact-edit',
      component: DashboardContactEditPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
      },
    },
    {
      path:'/dashboard/',
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresLeadsAccess: true,
      },
      children: [
        {
          path: 'leads/',
          name: 'dashboard-leads',
          component: DashboardLeadsPage,
        },
        {
          path: 'leads/:leadUuid',
          name: 'dashboard-lead-detail',
          component: DashboardLeadDetailPage,
        }
      ]
    },
    // {
    //   path: '/dashboard/leads',
    //   name: 'dashboard-leads',
    //   component: DashboardLeadsPage,
    //   meta: {

    //   },
    // },
    // {
    //   path: '/dashboard/leads/:leadUuid',
    //   name: 'dashboard-lead-detail',
    //   component: DashboardLeadDetailPage,
    //   meta: {
    //     authName: oidcAuth.authName,
    //     hideRightSidebar: true,
    //     requiresLeadsAccess: true,
    //   },
    // },
    {
      path: '/dashboard/todos',
      name: 'dashboard-todos',
      component: DashboardTodosPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
      },
    },
    {
      path: '/dashboard/envelopes',
      name: 'dashboard-envelopes',
      component: DashboardEnvelopesPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
      },
    },
    {
      path: '/recherche',
      name: 'search-global',
      component: SearchPage,
      meta: {
        authName: oidcAuth.authName,
        hideRightSidebar: true,
        requiresOmnisearch: true,
      },
    },
    ...v2Routes,
  ],
})

router.beforeEach(async (to, from, next) => {
  const boostCoreStore = useBoostCorePermissionStore()
  const isPublicRoute = to.meta.isPublic || to.meta.isOidcCallback || to.name === 'logout'

  // /auth/login: if a valid boostcore session is in store, bounce to per-tab URL.
  // OIDC alone is *not* enough — boostcore is the gatekeeper.
  if (to.path === '/auth/login') {
    if (boostCoreStore.user) {
      const redirectPath = sessionStorage.getItem('oidc_redirect') || '/'
      sessionStorage.removeItem('oidc_redirect')
      return next(redirectPath)
    }
    return next()
  }

  if (isPublicRoute) return next()

  // Boostcore is the only auth source of truth. Without a /me response,
  // nothing in the app is accessible — even with a fresh Azure token.
  // apiFetch transparently attempts recovery (refresh → silent renew → login)
  // before fetchMe gives up.
  if (!boostCoreStore.user) {
    const meResult = await boostCoreStore.fetchMe()
    if (!meResult) {
      // Save the *requested* URL — the user wanted to land here.
      if (!sessionStorage.getItem('oidc_redirect')) {
        sessionStorage.setItem('oidc_redirect', to.fullPath)
      }
      return next('/auth/login')
    }
    // fetchMe succeeded — init legacy stores (works for both MSAL and password).
    const userStore = useUserStore()
    if (!userStore.userConnected) {
      await userStore.getUserByEmail(meResult.user.email)
      if (userStore.userConnected) {
        userStore.getAllUsers()
        useSelectionsStore().getAll()
        useCampaignStore().getManagerCampaigns()
        useCampaignStore().getMyPrivateCampaign()
        useKpiStore().getMyKpisOpportunities()
        useTitleStore().getTitles()
      }
    }
  }

  // Permission gates (boostCoreStore.user is guaranteed non-null here).
  if (to.meta.requiresOrderAccess && !boostCoreStore.canAccessOrders) return next('/')
  if (to.meta.requiresOrderCreate && !boostCoreStore.canCreateOrder) return next('/dashboard/orders')
  if (to.meta.requiresLeadsAccess && !boostCoreStore.canAccessLeads) return next('/')
  if (to.meta.requiresRegulationAccess && !boostCoreStore.canAccessRegulations) return next('/')
  if (to.meta.requiresVariableAccess && !boostCoreStore.canAccessVariables) return next('/')
  if (to.meta.requiresEligibilityAccess && !boostCoreStore.canAccessEligibility) return next('/')
  if (to.meta.requiresSuperAdmin && !boostCoreStore.include('SUPER_ADMIN')) return next('/')
  if (to.meta.requiresUserMaster && !boostCoreStore.include('USER_MASTER')) return next('/')
  if (
    to.meta.requiresTicketDevAdmin &&
    !boostCoreStore.include('TICKETDEV_ADMIN') &&
    !boostCoreStore.include('SUPER_ADMIN')
  )
    return next('/')

  if (to.meta.requiresOmnisearch && !boostCoreStore.hasFlag('OMNISEARCH')) return next('/')

  if (
    typeof to.meta.requiresFeatureFlag === 'string' &&
    !boostCoreStore.hasFlag(to.meta.requiresFeatureFlag)
  )
    return next('/')

  if (
    to.meta.requiresDeliveryAdmin &&
    !boostCoreStore.can('DELIVERY_ADMIN') &&
    !boostCoreStore.include('SUPER_ADMIN')
  )
    return next('/')

  if (to.meta.requiresSalesRead && !boostCoreStore.can('SALES_READ')) return next('/')

  if (to.meta.requiresH3aStats && !boostCoreStore.can('H3A_STATS')) return next('/')

  // Routes mixtes V1/V2 : les pages V1 restent accessibles en mode v2 — elles sont
  // rendues dans le shell ApyLayout (cf App.vue, sélection du layout par `effectiveMode`).
  // Les routes 'v2' restent réservées au mode v2 ; `effectiveMode` retombe en V1 si le
  // flag LAYOUT_APY est absent, donc cette garde fait aussi office de feature gate.
  // En mode v2, la home V1 bascule vers la home V2 (point d'entrée cohérent).
  const layoutModeStore = useLayoutModeStore()
  const routeVersion = (to.meta.version as 'v1' | 'v2' | 'global' | undefined) ?? 'v1'
  if (routeVersion === 'v2' && layoutModeStore.effectiveMode !== 'v2') return next(HOME_PATH_V1)
  if (to.path === HOME_PATH_V1 && layoutModeStore.effectiveMode === 'v2') return next(HOME_PATH_V2)

  next()
})

// Note: oidcAuth.useRouter(router) removed — our custom beforeEach handles
// both MSAL and password auth. The OIDC library guard would force a redirect
// to Azure AD on any route with meta.authName, breaking password login.

export default router
